import commonConstants from "$/data/common";
import { stipplingStyling, stipplingStylingInverted } from "$/util/background";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import FaqSection from "./FaqSection";
import HomeFeaturesSection from "./HomeFeaturesSection";
import HomeTestimonialsSection from "./HomeTestimonialsSection";
import Section from "./Section";

function HomeWalkthroughSection() {
  const theme = useTheme();

  return (
    <>
      <Section
        size="auto"
        borderTop="0px solid"
        sx={{
          position: "relative",
          pt: 10,
        }}
      >
        <Container>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              p: {
                xs: 1,
                md: 4,
              },
            }}
          >
            <CardContent>
              <Grid container spacing={4} mt={0}>
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    borderRight: {
                      xs: "none",
                      md: `1px solid ${theme.palette.divider}`,
                    },
                    borderBottom: {
                      xs: `1px solid ${theme.palette.divider}`,
                      md: "none",
                    },
                    pt: "0 !important",
                    pr: {
                      xs: 0,
                      md: 2,
                    },
                  }}
                >
                  <Typography variant="overline">Getting Started</Typography>
                  <Typography
                    variant="h2"
                    variantMapping={{ h2: "h4" }}
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    How it Works
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 4, fontSize: 24 }}>
                    Getting started is easy! Join our community, browse &amp; favorite
                    designs, and get {commonConstants.FREE_CREDITS} free credits to
                    start generating.
                  </Typography>
                  <Typography variant="hero" color="primary">
                    1.5M+
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 4 }}>
                    Users and growing by the day
                  </Typography>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Stack direction="column">
                    <Stack direction="row" spacing={4}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: { xs: 80, md: 124 },
                        }}
                      >
                        <img
                          src="/images/temporary-tattoos/booking.webp"
                          alt="Sign Up"
                          width="83.2"
                          height="89.6"
                        />
                      </Box>
                      <Box py={3}>
                        <Typography variant="h4">Sign Up</Typography>
                        <Typography variant="body1">
                          Create an account and join our community of 1 Million+. Get{" "}
                          {commonConstants.FREE_CREDITS} free credits to start.
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={4}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: { xs: 80, md: 124 },
                        }}
                      >
                        <img
                          src="/images/temporary-tattoos/remove-patch.webp"
                          alt="Create"
                          width="80"
                          height="134"
                        />
                      </Box>
                      <Box py={3}>
                        <Typography variant="h4">Create</Typography>
                        <Typography variant="body1">
                          Use your free credits to generate tattoos with our AI tattoo
                          designer. Need more? <Link to="/pricing">Subscribe</Link> for
                          unlimited designs.
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={4}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: { xs: 80, md: 124 },
                        }}
                      >
                        <img
                          src="/images/temporary-tattoos/peel-and-stick.webp"
                          alt="Try It On"
                          width="75"
                          height="111"
                        />
                      </Box>
                      <Box py={3}>
                        <Typography variant="h4">Get Tatted</Typography>
                        <Typography variant="body1">
                          Share your designs with your tattoo artist and collaborate on
                          making your perfect tattoo a reality.
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
        <Box
          sx={{
            ...stipplingStylingInverted,
            height: "100%",
            position: "absolute",
            width: "100%",
            top: 0,
            zIndex: -1,
          }}
        />
      </Section>

      <Section bgColor="default" size="medium" borderTop="0px solid">
        <HomeFeaturesSection />
      </Section>

      <Section
        bgColor="light"
        size="medium"
        sx={{
          backgroundColor: "#F7F7F7",
        }}
      >
        <HomeTestimonialsSection />
      </Section>

      <Section
        size="medium"
        sx={{
          backgroundColor: "#F7F7F7",
          ...stipplingStyling,
          borderTop: "1px solid #E0E0E0",
        }}
      >
        <Container>
          <Typography variant="overline" color="inherit">
            BlackInk.AI in the News
          </Typography>
          <Typography variant="h2" fontWeight={500}>
            Press Spotlight
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={4} mt={2}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                p: 4,
              }}
            >
              <CardContent sx={{ position: "relative" }}>
                <img
                  src="/images/inked-logo.png"
                  alt="Inked Magazine"
                  width={86.5}
                  height={32.5}
                  style={{ maxWidth: 86.5, display: "block", aspectRatio: 1 }}
                />
                <Typography variant="overline">Inked Magazine</Typography>
                <Typography variant="h6" variantMapping={{ h6: "h5" }} mt={2}>
                  The Intelligent Tattoos of Tomorrow
                </Typography>
                <Typography variant="body1" style={{ marginTop: "15px" }}>
                  BlackInk AI replaces the daunting task of browsing the web, Instagram,
                  or Pinterest for hours on end. Once the client finds the platform they
                  enjoy most, they describe the art they are after in a few short words,
                  and the image is generated in less than a minute...
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  href="https://inkedmag.com/art/the-intelligent-tattoos-of-tomorrow/"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  color="inherit"
                >
                  Read More
                </Button>
              </CardActions>
            </Card>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                p: 4,
              }}
            >
              <CardContent sx={{ position: "relative" }}>
                <img
                  src="/images/the-information-logo.svg"
                  alt="The Information"
                  width={167.5}
                  height={28}
                  style={{ maxWidth: 167.5, display: "block", aspectRatio: 1 }}
                />
                <Typography variant="overline">Technology News</Typography>
                <Typography variant="h6" variantMapping={{ h6: "h5" }} mt={2}>
                  The Next Platform for AI-Generated Art: Your Bicep
                </Typography>
                <Typography variant="body1" style={{ marginTop: "15px" }}>
                  Marina Rosa walked into a neighborhood tattoo parlor and laid her left
                  arm on the table. Rosa then asked artist Nadine Guerra to give her a
                  tattoo made up of various elements, including a 1950s-style female
                  cyborg, an abstract squiggle, and four small items that look like
                  stereo speakers. The art was then permanently inked onto Rosa&apos;s
                  skin. But Rosa didn&apos;t design it—artificial intelligence did.{" "}
                  <br />
                  <br />
                  The design was the creation of BlackInk.AI...
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  href="https://www.theinformation.com/articles/the-next-platform-for-ai-generated-art-your-bicep"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  color="inherit"
                >
                  Read More
                </Button>
              </CardActions>
            </Card>
          </Stack>
        </Container>
      </Section>
      <Section bgColor="dark" borderTop="0px solid" size="medium">
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={4}>
              <Typography variant="h3" color="background.default" mb={2}>
                Frequently Asked Questions
              </Typography>
              <Typography variant="body1" color="background.default">
                If you have a question that&apos;s not listed here, please contact
                support at info@blackink.ai.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <FaqSection color="#fff" />
            </Grid>
          </Grid>
        </Container>
      </Section>
    </>
  );
}

export default HomeWalkthroughSection;
